import { useAuth } from '@clerk/clerk-react';
import {
  PageContainer,
  Button,
  DataTable,
  type DataTableColumn,
  ConfirmDialog,
} from '@startupquickstart/frontend';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  SkipForward,
  PlayCircle,
  Download,
  Ban,
} from 'lucide-react';
import { toast } from '@startupquickstart/frontend';
import { cn } from '@/lib/utils';
import { CsvImportWizard } from '@/components/import';

interface ImportSession {
  id: string;
  entityType: 'attraction' | 'neighborhood' | 'tour';
  fileName: string;
  totalRows: number;
  successCount: number;
  errorCount: number;
  skippedCount: number;
  status: string;
}

interface ImportRow {
  rowNumber: number;
  status: 'pending' | 'success' | 'error' | 'skipped';
  rawData: Record<string, unknown>;
  createdEntityId: string | null;
  errorMessage: string | null;
  processedAt: string | null;
  entity: { id: string; name: string } | null;
  matchedEntity?: { id: string; name: string } | null; // For duplicates - what existing entity it matched
}

const entityTypeLabels: Record<string, string> = {
  attraction: 'Attractions',
  neighborhood: 'Neighborhoods',
  tour: 'Tours',
};

const entityRoutes: Record<string, string> = {
  attraction: '/attractions',
  neighborhood: '/neighborhoods',
  tour: '/tours',
};

function RowStatusBadge({ status }: { status: string }) {
  const config: Record<
    string,
    { className: string; icon: React.ReactNode }
  > = {
    pending: {
      className: 'bg-muted text-muted-foreground',
      icon: <Clock className="h-3 w-3" />,
    },
    success: {
      className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
      icon: <CheckCircle className="h-3 w-3" />,
    },
    error: {
      className: 'bg-destructive/10 text-destructive',
      icon: <XCircle className="h-3 w-3" />,
    },
    skipped: {
      className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
      icon: <SkipForward className="h-3 w-3" />,
    },
  };

  const { className, icon } = config[status] || {
    className: 'bg-muted text-muted-foreground',
    icon: null,
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize',
        className
      )}
    >
      {icon}
      {status}
    </span>
  );
}

export function ImportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();

  // Check if we should auto-open the resume wizard (from list page "Resume" action)
  const shouldAutoResume = (location.state as { resume?: boolean })?.resume === true;

  const [session, setSession] = useState<ImportSession | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [showResumeWizard, setShowResumeWizard] = useState(shouldAutoResume);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const limit = 50;

  const fetchSessionRows = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String((page - 1) * limit),
      });
      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`/api/v1/import/sessions/${id}/rows?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Import session not found');
          navigate('/import');
          return;
        }
        throw new Error('Failed to fetch session rows');
      }

      const result = await response.json();
      setSession(result.data.session);
      setRows(result.data.rows);
      setTotal(result.pagination.total);
    } catch (error) {
      console.error('Failed to fetch session rows:', error);
      toast.error('Failed to load import details');
    } finally {
      setIsLoading(false);
    }
  }, [id, getToken, navigate, page, statusFilter]);

  useEffect(() => {
    fetchSessionRows();
  }, [fetchSessionRows]);

  const handleDownloadCsv = useCallback(async () => {
    if (!id) return;
    try {
      const token = await getToken();
      const response = await fetch(`/api/v1/import/sessions/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = session?.fileName || 'import.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download CSV');
    }
  }, [id, getToken, session?.fileName]);

  const handleCancel = useCallback(async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/v1/import/sessions/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to cancel import');
      }
      toast.success('Import cancelled');
      setShowCancelConfirm(false);
      fetchSessionRows();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel import');
    } finally {
      setIsCancelling(false);
    }
  }, [id, getToken, fetchSessionRows]);

  const columns: DataTableColumn<ImportRow>[] = useMemo(
    () => [
      {
        key: 'rowNumber',
        label: 'Row',
        sortable: false,
        format: (value: number) => (
          <span className="text-muted-foreground font-mono text-sm">{value}</span>
        ),
      },
      {
        key: 'rawData',
        label: 'Input Name',
        sortable: false,
        format: (value: Record<string, unknown>) => {
          const name = value?.name as string;
          return name ? (
            <span className="font-medium">{name}</span>
          ) : (
            <span className="text-muted-foreground italic">No name</span>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        format: (value: string) => <RowStatusBadge status={value} />,
      },
      {
        key: 'entity',
        label: 'Result',
        sortable: false,
        disableRowClick: true,
        format: (value: { id: string; name: string } | null, row: ImportRow) => {
          if (!session) return <span className="text-muted-foreground">—</span>;
          const route = entityRoutes[session.entityType];

          // Success - show created entity
          if (value && row.status === 'success') {
            return (
              <Link
                to={`${route}/${value.id}`}
                className="text-primary hover:underline inline-flex items-center gap-1 font-medium"
              >
                {value.name}
                <ExternalLink className="h-3 w-3" />
              </Link>
            );
          }

          // Skipped - show matched entity if available
          if (row.status === 'skipped' && row.matchedEntity) {
            return (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Duplicate of</span>
                <Link
                  to={`${route}/${row.matchedEntity.id}`}
                  className="text-amber-600 dark:text-amber-400 hover:underline inline-flex items-center gap-1 font-medium"
                >
                  {row.matchedEntity.name}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            );
          }

          // Skipped without match info
          if (row.status === 'skipped') {
            return <span className="text-muted-foreground text-sm">Skipped (duplicate)</span>;
          }

          // Error - show error message
          if (row.status === 'error' && row.errorMessage) {
            return <span className="text-destructive text-sm">{row.errorMessage}</span>;
          }

          // Pending
          if (row.status === 'pending') {
            return <span className="text-muted-foreground text-sm">Pending</span>;
          }

          return <span className="text-muted-foreground">—</span>;
        },
      },
    ],
    [session]
  );

  const emptyState = (
    <div className="py-16 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
        <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
      </div>
      <p className="text-lg font-medium tracking-tight">No rows found</p>
      <p className="text-sm text-muted-foreground mt-1">
        {statusFilter ? `No rows with status "${statusFilter}"` : 'This import session has no rows.'}
      </p>
    </div>
  );

  const pagination = {
    page,
    limit,
    total,
    totalPages: Math.ceil(total / limit),
    hasNext: page * limit < total,
    hasPrev: page > 1,
  };

  const filterOptions = [
    { key: undefined, label: 'All', icon: null },
    { key: 'success', label: 'Imported', icon: CheckCircle, iconClass: 'text-emerald-600 dark:text-emerald-400' },
    { key: 'error', label: 'Errors', icon: XCircle, iconClass: 'text-destructive' },
    { key: 'skipped', label: 'Skipped', icon: SkipForward, iconClass: 'text-amber-600 dark:text-amber-400' },
  ];

  // Show resume wizard
  if (showResumeWizard && session) {
    return (
      <PageContainer>
        <CsvImportWizard
          onCancel={() => setShowResumeWizard(false)}
          onImportComplete={() => {
            setShowResumeWizard(false);
            fetchSessionRows();
          }}
          resumeSessionId={session.id}
          resumeEntityType={session.entityType}
        />
      </PageContainer>
    );
  }

  const canResume = session?.status === 'awaiting_confirmation' || session?.status === 'validated';
  const canCancel = ['pending', 'awaiting_confirmation', 'validated'].includes(session?.status || '');

  return (
    <PageContainer>
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/import')}
              className="rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl font-semibold tracking-tight">Import Details</h1>
              {session && (
                <p className="text-muted-foreground mt-1">
                  {session.fileName} — {entityTypeLabels[session.entityType]}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadCsv}>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
              {canCancel && (
                <Button variant="outline" size="sm" onClick={() => setShowCancelConfirm(true)}>
                  <Ban className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              )}
              {canResume && (
                <Button onClick={() => setShowResumeWizard(true)} className="gap-2">
                  <PlayCircle className="h-4 w-4" />
                  Resume Import
                </Button>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          {session && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total - Neutral */}
              <div className="rounded-xl bg-muted/30 p-5">
                <div className="text-3xl font-semibold tracking-tight">{session.totalRows}</div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-1">
                  Total Rows
                </div>
              </div>

              {/* Success - Green */}
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/50 p-5">
                <div className="text-3xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400">
                  {session.successCount}
                </div>
                <div className="text-xs font-medium uppercase tracking-wider text-emerald-600/70 dark:text-emerald-400/70 mt-1">
                  Imported
                </div>
              </div>

              {/* Errors - Red */}
              <div className="rounded-xl bg-destructive/10 p-5">
                <div className="text-3xl font-semibold tracking-tight text-destructive">
                  {session.errorCount}
                </div>
                <div className="text-xs font-medium uppercase tracking-wider text-destructive/70 mt-1">
                  Errors
                </div>
              </div>

              {/* Skipped - Amber */}
              <div className="rounded-xl bg-amber-50 dark:bg-amber-950/50 p-5">
                <div className="text-3xl font-semibold tracking-tight text-amber-600 dark:text-amber-400">
                  {session.skippedCount}
                </div>
                <div className="text-xs font-medium uppercase tracking-wider text-amber-600/70 dark:text-amber-400/70 mt-1">
                  Skipped
                </div>
              </div>
            </div>
          )}

          {/* Segmented Filter Controls */}
          <div className="inline-flex rounded-lg bg-muted/50 p-1 gap-1">
            {filterOptions.map((filter) => {
              const isActive = statusFilter === filter.key;
              const Icon = filter.icon;
              return (
                <button
                  key={filter.key ?? 'all'}
                  onClick={() => {
                    setStatusFilter(filter.key);
                    setPage(1);
                  }}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2',
                    isActive
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {Icon && <Icon className={cn('h-4 w-4', !isActive && filter.iconClass)} />}
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rows Table */}
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <DataTable<ImportRow>
            columns={columns}
            data={rows}
            loading={isLoading}
            emptyState={emptyState}
            pagination={pagination}
            onPageChange={setPage}
            skeletonRows={10}
          />
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title="Cancel Import"
        description="Are you sure you want to cancel this import? This action cannot be undone and any pending rows will not be processed."
        confirmText="Cancel Import"
        cancelText="Keep Import"
        variant="destructive"
        loading={isCancelling}
        onConfirm={handleCancel}
      />
    </PageContainer>
  );
}
